import React, { useState } from "react";
import { Box, Typography, TextField, Button, MenuItem } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import TinkerLabListAPIs from "utilities/api/TinkerLabListAPIs";
import BackgroundImage from "../../assets/background.png";
import Logo1 from "../../utilities/dummy_assets/MBF_Logo.png";
import mapITlogo from "../../utilities/dummy_assets/svgs/MapIt_Logo.svg";
import * as Yup from "yup";
import { toast } from "react-toastify";
import PageTransition from "components/PageTransition/PageTransition";

const studentValidationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .matches(/^[A-Za-z\s]+$/, "Name can only contain letters and spaces")
    .required("Name is required"),
  phone: Yup.string()
    .matches(/^\d{10}$/, "Phone number must be 10 digits")
    .required("Phone No. is required"),
  gender: Yup.string().required("Gender is required"),
});

/**
 * AdminOrgDetail Component
 * Handles the collection of admin organizational details during registration
 * Captures: email, name, phone, gender, and profile picture
 */
const StudentDetail = () => {
  // Navigation and location hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Extract data from previous page
  const capturedImage =
    location.state?.capturedImage || "https://via.placeholder.com/166x154";
  const position = location.state?.position || "";

  // Form field states
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");

  // Error tracking for form validation
  const [errors, setErrors] = useState({
    email: "",
    name: "",
    phone: "",
    gender: "",
  });

  /**
   * Handle form submission
   * Validates all required fields and navigates to next step
   */
  const handleNext = async () => {
    try {
      // Validate current form values using Yup
      await studentValidationSchema.validate(
        { email, name, phone, gender },
        { abortEarly: false }, // validate all fields at once
      );
      //  Fetch all TLs to check valid domains
      const allTLs = await TinkerLabListAPIs.TinkerLabsListGet();
      // Extract domain from email to fetch associated Tinker Lab
      const ldap_end = "@" + email.split("@")[1];

      // Check if domain exists in any TL’s accepted_ldaps
      const isAllowedDomain = allTLs.some((lab) =>
        lab.accepted_ldaps.some((domain) =>
          ldap_end.endsWith(domain.toLowerCase()),
        ),
      );
      if (!isAllowedDomain) {
        toast.error(
          "Your email domain is not associated with any Tinkerers Lab.",
        );
        return;
      }
      const tl_name = await TinkerLabListAPIs.fetchTL(ldap_end);

      // Navigate to next page with all collected data
      navigate("/more-detail", {
        state: { ...location.state, email, name, phone, gender, tl_name },
      });
    } catch (validationError) {
      // Map Yup errors to your errors state
      const newErrors = { email: "", name: "", phone: "", gender: "" };
      if (validationError.inner) {
        validationError.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
      }
      setErrors(newErrors);
    }
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "auto",
        backgroundColor: { xs: "#FFFFFF", sm: "transparent" },
        backgroundImage: { xs: "none", sm: `url(${BackgroundImage})` },
        backgroundSize: "cover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        px: 2,
        py: 4,
        boxSizing: "border-box",
      }}
    >
      {/* Top Left Logo */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 10, sm: 24 },
          left: { xs: 10, sm: 32 },
        }}
      >
        <img
          src={Logo1}
          alt="MBF Logo"
          style={{ height: "auto", width: "auto", maxHeight: "50px" }}
        />
      </Box>

      {/* Top Right Link */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 10, sm: 24 },
          right: { xs: 10, sm: 32 },
        }}
      >
        <Typography
          component="a"
          href="/student"
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            color: "#007BFF",
            textDecoration: "underline",
            fontWeight: 500,
          }}
        >
          Go to Admin site
        </Typography>
      </Box>

      {/* Main Card */}
      <PageTransition
        sx={{
          width: "100%",
          maxWidth: "650px",
          background: "#FFFFFF",
          borderRadius: "24px",
          padding: "32px",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        {/* Profile Picture with Edit Button */}
        <Box sx={{ position: "relative" }}>
          <img
            src={capturedImage}
            alt="Profile"
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "8px",
              objectFit: "cover",
            }}
          />
          <Box
            onClick={() => navigate("/profile-picture")}
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "32px",
              height: "32px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: 1,
              cursor: "pointer",
            }}
          >
            <EditIcon fontSize="small" />
          </Box>
        </Box>

        {/* Registration Form Fields */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, width: "100%" }}>
          {/* Email Field */}
          <Box sx={{ flex: "1 1 48%" }}>
            {errors.email && (
              <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}>
                {errors.email}
              </Typography>
            )}
            <TextField
              fullWidth
              required
              label="Email Address"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: false });
              }}
              error={errors.email}
            />
          </Box>

          {/* Name Field */}
          <Box sx={{ flex: "1 1 48%" }}>
            {errors.name && (
              <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}>
                {errors.name}
              </Typography>
            )}
            <TextField
              fullWidth
              required
              label="Name"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: false });
              }}
              error={errors.name}
            />
          </Box>

          {/* Phone Number Field */}
          <Box sx={{ flex: "1 1 48%" }}>
            {errors.phone && (
              <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}>
                {errors.phone}
              </Typography>
            )}
            <TextField
              fullWidth
              required
              label="Phone No."
              placeholder="Enter phone no."
              value={phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 10) setPhone(value);
                if (errors.phone) setErrors({ ...errors, phone: false });
              }}
              error={errors.phone}
            />
          </Box>

          {/* Gender Selection Field */}
          <Box sx={{ flex: "1 1 48%" }}>
            {errors.gender && (
              <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}>
                {errors.gender}
              </Typography>
            )}
            <TextField
              select
              fullWidth
              required
              label="Gender"
              value={gender}
              onChange={(e) => {
                setGender(e.target.value);
                if (errors.gender) setErrors({ ...errors, gender: false });
              }}
              error={errors.gender}
            >
              <MenuItem value="" disabled>
                Select Gender
              </MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", width: "100%", gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            sx={{ borderRadius: "12px" }}
            onClick={() => navigate("/login")}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#007BFF",
              color: "#fff",
              borderRadius: "12px",
              "&:hover": { backgroundColor: "#98c6f5" },
            }}
            onClick={handleNext}
          >
            Next
          </Button>
        </Box>

        <Typography sx={{ fontSize: "14px", color: "#3F3F3F" }}>
          Already have an account?{" "}
          <a
            href="/login"
            style={{ color: "#007BFF", textDecoration: "underline" }}
          >
            Log in here
          </a>
        </Typography>
      </PageTransition>

      {/* Powered by Section */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.3rem",
          padding: { xs: "8px", sm: "12px" },
          backgroundColor: "transparent",
        }}
      >
        <Typography fontSize={{ xs: "11px", sm: "12px" }} color="#666">
          Powered by
        </Typography>
        <img src={mapITlogo} alt="MapIT Logo" style={{ height: "30px" }} />
      </Box>
    </Box>
  );
};

export default StudentDetail;
